import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { NavigationProvider } from "@/contexts/NavigationContext";
import { ProjectProvider } from "@/contexts/ProjectContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import Index from "./pages/Index";
import { LEGACY_REDIRECTS } from "@/components/LegacyRedirects";

// Lazy-load toast components - not needed for FCP
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load all non-homepage routes to reduce initial bundle size (~576 KiB savings)
const About = lazy(() => import("./pages/About"));
const Eventbau = lazy(() => import("./pages/Eventbau"));
const Aktivierung = lazy(() => import("./pages/Aktivierung"));
const Werbetechnik = lazy(() => import("./pages/Werbetechnik"));
const Posm = lazy(() => import("./pages/Posm"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Impressum = lazy(() => import("./pages/Impressum"));
const AGB = lazy(() => import("./pages/AGB"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const Admin = lazy(() => import("./pages/Admin"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}><Toaster /></Suspense>
      <Suspense fallback={null}><Sonner /></Suspense>
      <BrowserRouter>
        <NavigationProvider>
          <LanguageProvider>
            <ProjectProvider>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<Suspense fallback={null}><About /></Suspense>} />
                  <Route path="/eventbau" element={<Suspense fallback={null}><Eventbau /></Suspense>} />
                  <Route path="/aktivierung" element={<Suspense fallback={null}><Aktivierung /></Suspense>} />
                  <Route path="/werbetechnik" element={<Suspense fallback={null}><Werbetechnik /></Suspense>} />
                  <Route path="/posm" element={<Suspense fallback={null}><Posm /></Suspense>} />
                  <Route path="/project/:id" element={<Suspense fallback={null}><ProjectDetail /></Suspense>} />
                  <Route path="/impressum" element={<Suspense fallback={null}><Impressum /></Suspense>} />
                  <Route path="/agb" element={<Suspense fallback={null}><AGB /></Suspense>} />
                  <Route path="/datenschutz" element={<Suspense fallback={null}><Datenschutz /></Suspense>} />
                </Route>
                <Route path="/login" element={<Suspense fallback={null}><Login /></Suspense>} />
                <Route path="/change-password" element={<Suspense fallback={null}><ChangePassword /></Suspense>} />
                <Route path="/admin" element={<Suspense fallback={null}><Admin /></Suspense>} />
                {/* Legacy redirects from old CMS */}
                {LEGACY_REDIRECTS.map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                {/* Marketing-audit fixes: known broken inbound paths */}
                <Route path="/work" element={<Navigate to="/aktivierung" replace />} />
                <Route path="/services" element={<Navigate to="/aktivierung" replace />} />
                <Route path="/en" element={<Navigate to="/" replace />} />
                <Route path="/en/*" element={<Navigate to="/" replace />} />
                {/* Catch-all legacy paths with trailing slashes */}
                <Route path="/portfolio-category/*" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
              </Routes>
            </ProjectProvider>
          </LanguageProvider>
        </NavigationProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
